// @ts-check
/**
 * CUSTOM (AMANTÉ): Søgeforslag – supplerer Motions <predictive-search> (assets/theme.js røres ikke).
 * Markup: snippets/custom-predictive-search.liquid (mode 'panel' og 'results').
 *
 * - Populær-panelet vises af CSS, når søgningen er åben og Motions resultater ikke vises.
 * - Tømmes feltet, skjules gamle resultater (Motion lader dem stå) → panelet vises igen.
 * - Genåbnes søgningen med tekst i feltet, hentes resultaterne igen (Motion tømmer dem ved luk).
 * - Ryd-knap, piletaster op/ned mellem links, status til skærmlæsere.
 * - Motion beder om 3 af hver; vi beder om 8 (Liquid viser 4, eller 8 når der kun er produkter).
 */

const PRODUCT_REQUEST_LIMIT = 8;

/**
 * Næste fokus-indeks ved piletast. -1 = søgefeltet.
 * @param {number} current nuværende indeks (-1 = feltet)
 * @param {number} delta +1 (ned) eller -1 (op)
 * @param {number} length antal links
 * @returns {number}
 */
function nextIndex(current, delta, length) {
  if (length <= 0) return -1;
  const next = current + delta;
  if (next < -1) return -1;
  if (next > length - 1) return length - 1;
  return next;
}

/**
 * Status-tekst til skærmlæsere.
 * @param {number} count antal links i resultaterne
 * @param {string} countText tekst med %COUNT%
 * @param {string} noneText tekst ved ingen resultater
 * @returns {string}
 */
function statusText(count, countText, noneText) {
  return count > 0 ? countText.replace('%COUNT%', String(count)) : noneText;
}

/**
 * Er resultaterne for et andet søgeord end det, der står i feltet nu?
 * @param {string} resultTerms søgeordet resultaterne blev lavet til
 * @param {string} inputValue feltets nuværende værdi
 * @returns {boolean}
 */
function isStale(resultTerms, inputValue) {
  const norm = (/** @type {string} */ s) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  return norm(resultTerms) !== norm(inputValue);
}

/** Motion beder om 3 resultater af hver type; vi beder om flere (uden at ændre theme.js). */
function patchRequestLimit() {
  customElements.whenDefined('predictive-search').then(() => {
    const ctor = /** @type {any} */ (customElements.get('predictive-search'));
    const proto = ctor && ctor.prototype;
    if (!proto || typeof proto.paramUrl !== 'function' || proto.customLimitPatched) return;
    const original = proto.paramUrl;
    proto.paramUrl = function (/** @type {Record<string, any>} */ obj) {
      if (obj && 'resources[limit]' in obj) obj['resources[limit]'] = PRODUCT_REQUEST_LIMIT;
      return original.call(this, obj);
    };
    proto.customLimitPatched = true;
  });
}

class CustomSearchPanel extends HTMLElement {
  /** Ren logik – kan testes isoleret via customElements.get('custom-search-panel').logic */
  static logic = { nextIndex, statusText, isStale };

  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    /** @type {HTMLElement | null} */
    this.root = this.closest('predictive-search');
    if (!this.root) return;
    /** @type {HTMLInputElement | null} */
    this.input = this.root.querySelector('input[type="search"]');
    /** @type {HTMLElement | null} */
    this.results = this.root.querySelector('#predictive-search');
    /** @type {HTMLButtonElement | null} */
    this.clearBtn = this.root.querySelector('[data-custom-search-clear]');
    /** @type {HTMLElement | null} */
    this.status = this.root.querySelector('[data-custom-search-status]');
    if (!this.input || !this.results) return;

    this.root.classList.add('custom-search-enabled');

    // Motion kalder input.focus() uden preventScroll → siden ruller, så feltet skubbes op (iOS + tastatur).
    const input = this.input;
    input.focus = (/** @type {FocusOptions | undefined} */ options) =>
      HTMLInputElement.prototype.focus.call(input, { ...options, preventScroll: true });

    // Ryd-knappen hører til feltet (snippet'et kan kun placeres efter formens felter).
    const wrap = this.root.querySelector('.search__input-wrap');
    if (wrap && this.clearBtn) wrap.appendChild(this.clearBtn);

    this.input.addEventListener('input', () => this.onInput());
    this.clearBtn?.addEventListener('click', () => this.clear());
    this.root.addEventListener('keydown', (e) => this.onKeydown(e));

    document.addEventListener('predictive-search:open', (e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      if (detail && detail.context !== this.root?.dataset.context) return;
      this.onOpen();
    });

    // Motion skriver resultaterne ind som innerHTML og viser/skjuler med style.display.
    new MutationObserver(() => this.onResultsChange()).observe(this.results, {
      childList: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    window.addEventListener('resize', () => this.updateMaxHeight());
  }

  onOpen() {
    this.updateMaxHeight();
    this.syncClear();
    // Motion tømmer resultaterne ved luk – står der tekst i feltet, hentes de igen.
    if (this.input && this.input.value.trim()) {
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    // Panelet glider ind med headeren; mål igen når animationen er færdig.
    window.setTimeout(() => this.updateMaxHeight(), 350);
  }

  onInput() {
    this.syncClear();
    if (!this.input || !this.results) return;
    if (!this.input.value.trim()) {
      this.results.style.display = 'none';
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      if (this.status) this.status.textContent = '';
    }
  }

  onResultsChange() {
    if (!this.results || !this.input) return;

    // Motion viser svar, der ankommer for sent (fx "sof", efter feltet er tømt eller ændret).
    const root = /** @type {HTMLElement | null} */ (this.results.querySelector('[data-custom-search-results]'));
    if (root && isStale(root.dataset.terms || '', this.input.value)) {
      if (!this.input.value.trim()) {
        this.results.style.display = 'none';
        this.results.innerHTML = '';
        return;
      }
      // Hent igen for det, der faktisk står i feltet – højst én gang pr. værdi (ingen løkker).
      if (this.refetchedFor !== this.input.value) {
        this.refetchedFor = this.input.value;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }
    }

    const visible = this.results.style.display === 'block' && this.results.childElementCount > 0;
    this.input.setAttribute('aria-expanded', String(visible));
    if (visible) {
      this.updateMaxHeight();
      if (this.status) {
        const empty = !!this.results.querySelector('.custom-search__empty');
        const count = this.results.querySelectorAll('.custom-search__link, .custom-search__product-link').length;
        this.status.textContent = statusText(
          empty ? 0 : count,
          this.status.dataset.countText || '',
          this.status.dataset.noneText || ''
        );
      }
    }
  }

  syncClear() {
    if (this.clearBtn && this.input) this.clearBtn.hidden = !this.input.value;
  }

  clear() {
    if (!this.input) return;
    this.input.value = '';
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.focus();
  }

  /**
   * Desktop: panelet må ikke gå under skærmens kant (--custom-search-max-h).
   * Mobil/tablet: panelet fylder fra feltet til bunden af skærmen (CSS: 100lvh − --custom-search-top).
   * Tastaturet må IKKE ændre højden – ellers ses siden bag tastaturet (ejer 14/10), derfor ingen visualViewport.
   */
  updateMaxHeight() {
    if (!this.root) return;
    const anchor = this.root.querySelector('.search__input-wrap') || this.root;
    const top = anchor.getBoundingClientRect().bottom;
    const max = Math.max(200, Math.round(window.innerHeight - top - 8));
    this.root.style.setProperty('--custom-search-max-h', `${max}px`);
    // Panelet starter 10px under feltet (margin-top i CSS)
    this.root.style.setProperty('--custom-search-top', `${Math.round(top + 10)}px`);

    // Mobil: panelet går fra skærmkant til skærmkant (formen har sidemargin).
    const form = this.root.querySelector('form');
    if (form && window.innerWidth < 1024) {
      const left = form.getBoundingClientRect().left;
      this.root.style.setProperty('--custom-search-left', `${-Math.round(left)}px`);
      this.root.style.setProperty('--custom-search-width', `${document.documentElement.clientWidth}px`);
      this.root.style.setProperty('--custom-search-gutter', `${Math.max(16, Math.round(left))}px`);
    } else {
      this.root.style.removeProperty('--custom-search-left');
      this.root.style.removeProperty('--custom-search-width');
      this.root.style.removeProperty('--custom-search-gutter');
    }
  }

  /** @returns {HTMLElement[]} links i den synlige del (resultater eller populær-panelet) */
  focusables() {
    if (!this.results) return [];
    const resultsVisible = this.results.style.display === 'block';
    const scope = resultsVisible ? this.results : this;
    return /** @type {HTMLElement[]} */ (Array.from(scope.querySelectorAll('a[href]'))).filter(
      (el) => el.offsetParent !== null
    );
  }

  /** @param {KeyboardEvent} e */
  onKeydown(e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    if (!this.root || !this.root.classList.contains('is-active') || !this.input) return;
    const items = this.focusables();
    if (!items.length) return;
    const active = /** @type {HTMLElement | null} */ (document.activeElement);
    const current = active === this.input ? -1 : items.indexOf(/** @type {HTMLElement} */ (active));
    if (current === -1 && active !== this.input) return;
    e.preventDefault();
    const next = nextIndex(current, e.key === 'ArrowDown' ? 1 : -1, items.length);
    (next === -1 ? this.input : items[next]).focus();
  }
}

/**
 * Søgesiden: "Ændr søgning" / "Søg igen" (snippets/custom-search-page) åbner headerens søgning
 * med søgeordet udfyldt – forslagene hentes med det samme (onOpen). Uden JS: link til /search.
 */
document.addEventListener('click', (e) => {
  const target = /** @type {HTMLElement | null} */ (e.target instanceof Element ? e.target : null);
  const link = /** @type {HTMLElement | null} */ (target?.closest('[data-custom-search-edit]'));
  if (!link) return;
  const header = document.querySelector('predictive-search[data-context="header"]');
  const input = /** @type {HTMLInputElement | null} */ (header?.querySelector('input[type="search"]'));
  const opener = /** @type {HTMLElement | undefined} */ (
    Array.from(document.querySelectorAll('.js-search-header')).find((el) => /** @type {HTMLElement} */ (el).offsetParent !== null)
  );
  if (!input || !opener) return;
  e.preventDefault();
  input.value = link.dataset.terms || '';
  opener.click();
  window.setTimeout(() => {
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }, 200);
});

patchRequestLimit();
if (!customElements.get('custom-search-panel')) {
  customElements.define('custom-search-panel', CustomSearchPanel);
}
